import { inject, Injectable, OnDestroy } from '@angular/core';

import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, map, shareReplay, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import {
    AbstractRecordsStateService,
    AI_STATUS_FIELD_DEFINITION_DOTTED_KEY,
    ApiFragment,
    ApiQuery,
    BaseService,
    Combine,
    DottedKey,
    FACES_FIELD_DEFINITION_DOTTED_KEY,
    FacialRecognitionGuard,
    Family,
    FragmentId,
    HAS_FACES_FIELD_DEFINITION_DOTTED_KEY,
    Identifier,
    QueryPart,
    RecordRestrictions,
    RecordStatus,
    SetRecordAiStatus,
    SetRecordFaces,
    ZoneRestrictions
} from '@stylehaven/components';
import { SearchService } from '../api/search.service';

@Injectable({ providedIn: 'root' })
export class RecordHolderService extends BaseService implements OnDestroy {
    public record$: Observable<ApiFragment>;
    public recordParent$: Observable<ApiFragment | undefined>;

    private readonly loadingErrorState$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public loadingError$: Observable<boolean> = this.loadingErrorState$.asObservable();

    private readonly identifier$: ReplaySubject<Identifier> = new ReplaySubject<Identifier>(1);
    private readonly refresh$: ReplaySubject<void> = new ReplaySubject<void>(1);

    private readonly facialRecognitionGuard: FacialRecognitionGuard = inject(FacialRecognitionGuard);

    constructor(
        protected search: SearchService,
        protected recordsState: AbstractRecordsStateService
    ) {
        super();
        this.initialize();
    }

    private initialize(): void {
        this.record$ = combineLatest([this.identifier$, this.refresh$]).pipe(
            map(([identifier]) => identifier),
            switchMap(record => this.getRecord(record)),
            tap(() => this.setError(false)),
            catchError(this.handleGetRecordError.bind(this)),
            shareReplay(1)
        );

        this.refresh$.next();
    }

    // MED-5509: Sometimes the component is already inside the detail page (ex: intellectual-items-tab's child: fragment-master-details)
    //  thus must not use `loadRecord`, which would overwrite record$ for the main component and master-detail panel not open
    public getRecord(identifier: Identifier): Observable<ApiFragment> {
        // MED-5679: IncludeDeleted should be true since this is also used on Recycle-bin pages
        const baseRecord$ = this.search
            .getRecord(identifier, { includeDeleted: true })
            .pipe(take(1), takeUntil(this.destroy$));
        const aiRecord$ = this.getAiFields(identifier);

        return combineLatest([baseRecord$, aiRecord$]).pipe(
            map(([baseRecord, aiRecord]) => this.appendAiFields(baseRecord, aiRecord)),
            switchMap(record => this.appendSuggestions(record)),
            tap(record => this.recordsState.addRecord(record)),
            tap(record => this.search.registerConsultation(record.getFragmentId())),
            shareReplay(1)
        );
    }

    private getAiFields(identifier: Identifier): Observable<ApiFragment | null> {
        const hasFacialRecognitionActive$: Observable<boolean> = this.facialRecognitionGuard.hasAccess();

        return hasFacialRecognitionActive$.pipe(
            switchMap(isActive =>
                this.search.getRecord(identifier, {
                    includeDeleted: true,
                    fields: isActive
                        ? [
                              FACES_FIELD_DEFINITION_DOTTED_KEY,
                              HAS_FACES_FIELD_DEFINITION_DOTTED_KEY,
                              AI_STATUS_FIELD_DEFINITION_DOTTED_KEY
                          ]
                        : [AI_STATUS_FIELD_DEFINITION_DOTTED_KEY]
                })
            )
        );
    }

    private appendAiFields(record: ApiFragment, aiFacesRecord: ApiFragment | null): ApiFragment {
        if (!aiFacesRecord) {
            return record;
        }
        const modifiedRecord: ApiFragment = SetRecordFaces(record, aiFacesRecord);

        return SetRecordAiStatus(modifiedRecord, aiFacesRecord);
    }

    private appendSuggestions(apiFragment: ApiFragment): Observable<ApiFragment> {
        const suggestions: FragmentId[] | null = apiFragment.getRelationFragmentIdsOfType('HasAsSuggestion');

        return suggestions?.length
            ? this.getSuggestions(apiFragment.getFragmentId()).pipe(
                  map(suggestions => this.setSuggestions(apiFragment, suggestions))
              )
            : of(apiFragment);
    }

    private getSuggestions(fragmentId: FragmentId): Observable<ApiFragment[]> {
        return this.search.getAllRecords(
            new ApiQuery(
                [],
                Combine.And(
                    ZoneRestrictions.fullZoneSearch(),
                    new QueryPart({ field: 'Structural.Relations.IsSuggestedFor', values: [fragmentId] }),
                    new QueryPart({ field: 'Administrative.RecordType', values: ['ThesaurusSuggestion'] }),
                    RecordRestrictions.excludeRecordStatusesRestriction([RecordStatus.Published, RecordStatus.Rejected])
                ),
                ['Descriptive.Title', 'Structural.FieldDefinition', 'Internal.FragmentId'] as DottedKey[]
            ),
            {
                annotate: false
            }
        );
    }

    private setSuggestions(apiFragment: ApiFragment, suggestions: ApiFragment[]): ApiFragment {
        apiFragment.set(
            Family.Context,
            'Suggestions',
            suggestions.map(suggestion => suggestion.metadata)
        );
        return apiFragment;
    }

    private setError(errorState: boolean): void {
        this.loadingErrorState$.next(errorState);
    }

    private handleGetRecordError(error: Error): Observable<never> {
        this.setError(true);
        throw new Error(error.message);
    }

    public ngOnDestroy(): void {
        this.identifier$.complete();
        this.refresh$.complete();
        super.ngOnDestroy();
    }

    public loadRecord(identifier: Identifier): Observable<ApiFragment> {
        this.identifier$.next(identifier);
        return this.record$;
    }

    public refresh(): void {
        this.refresh$.next();
    }
}
